/** useEvalConfig — load and save an agent's optimization config.
 *
 * The config (optimization goal, weighted judge dimensions, pass threshold)
 * steers both the judge and the improver, so it's fetched once for the
 * Experiment tab and saved on demand.
 */

import { useCallback, useEffect, useState } from 'react';
import type { EvalConfig, EvalConfigUpdate } from '@/entities/eval';
import { getEvalApi } from './evalClient';

export interface UseEvalConfigResult {
  config: EvalConfig | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  reload: () => Promise<void>;
  save: (update: EvalConfigUpdate) => Promise<boolean>;
}

export function useEvalConfig(agentId: string): UseEvalConfigResult {
  const [config, setConfig] = useState<EvalConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setConfig(await getEvalApi().getConfig(agentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = useCallback(
    async (update: EvalConfigUpdate) => {
      setSaving(true);
      setError(null);
      try {
        setConfig(await getEvalApi().putConfig(agentId, update));
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save config');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [agentId],
  );

  return { config, loading, saving, error, reload, save };
}
